if (!customElements.get('product-form')) {
  class ProductForm extends HTMLElement {
    constructor() {
      super();
      if (this.hasChildNodes()) this.init();
    }

    init() {
      this.form = this.querySelector('.js-product-form');
      if (this.form) {
        this.cartDrawer = document.querySelector('cart-drawer');

        const fitment = this.form.querySelector('[data-fitment-kit]');
        const fitmentGuard = this.form.querySelector('[data-fitment-js-required]');
        if (theme.settings.afterAtc !== 'no-js' || fitment) {
          this.form.addEventListener('submit', this.handleSubmit.bind(this));
          if (fitmentGuard) {
            if (window.FitmentCart) {
              fitmentGuard.required = false;
            } else {
              document.addEventListener('fitment:ready', () => {
                if (window.FitmentCart) fitmentGuard.required = false;
              }, { once: true });
            }
          }
        }
      }
    }

    /**
     * Handles submission of the product form.
     * @param {object} evt - Event object.
     */
    async handleSubmit(evt) {
      if (evt.target.id === 'product-signup_form') return;

      evt.preventDefault();

      this.submitBtn = this.querySelector('[name="add"]');

      if (this.submitBtn.getAttribute('aria-disabled') === 'true') return;

      if (theme.settings.vibrateOnATC && window.navigator.vibrate) {
        window.navigator.vibrate(100);
      }

      this.errorMsg = null;
      this.setErrorMsgState();
      const fitmentPicker = this.form.querySelector('[data-fitment-kit]');
      const selectedFitment = fitmentPicker?.querySelector('input[type="radio"]:checked');
      if (fitmentPicker && (!selectedFitment || selectedFitment.disabled || !window.FitmentCart)) {
        this.setErrorMsgState(fitmentPicker.dataset.errorMessage);
        return;
      }
      const fitmentVariantId = selectedFitment?.value || '0';
      let addedSuccessfully = false;

      // Disable "Add to Cart" button until submission is complete.
      this.submitBtn.setAttribute('aria-disabled', 'true');
      this.submitBtn.classList.add('is-loading');

      const formData = new FormData(this.form);
      let sections = 'cart-icon-bubble';
      if (this.cartDrawer) {
        sections += `,${this.cartDrawer.closest('.shopify-section').id.replace('shopify-section-', '')}`;
      }

      formData.append('sections_url', window.location.pathname);
      formData.append('sections', sections);

      let requestBody = formData;
      try {
        if (fitmentPicker) requestBody = window.FitmentCart.createFitmentRequest(formData, fitmentVariantId);
      } catch (error) {
        this.setErrorMsgState(fitmentPicker.dataset.errorMessage);
        this.submitBtn.removeAttribute('aria-disabled');
        this.submitBtn.classList.remove('is-loading');
        return;
      }

      const fetchRequestOpts = {
        method: 'POST',
        headers: {
          Accept: 'application/javascript',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: requestBody
      };

      try {
        const oldCartResponse = await fetch(`${theme.routes.cart}.js`);
        if (!oldCartResponse.ok) throw new Error(oldCartResponse.status);
        const oldCartData = await oldCartResponse.json();

        const response = await fetch(theme.routes.cartAdd, fetchRequestOpts);
        const data = await response.json();
        let error = typeof data.description === 'string' ? data.description : data.message;
        if (data.errors && typeof data.errors === 'object') {
          error = Object.entries(data.errors).map((item) => item[1].join(', '));
        }

        if (data.status) this.setErrorMsgState(error);

        if (!response.ok || (fitmentPicker && (data.status || data.errors))) {
          if (fitmentPicker) this.setErrorMsgState(`${error || ''} ${fitmentPicker.dataset.errorMessage}`.trim());
          throw new Error(response.status);
        }
        if (fitmentVariantId !== '0' && Array.isArray(data.items)) {
          const mainLine = data.items.find((line) => String(line.variant_id) === String(formData.get('id')));
          if (!mainLine) throw new Error('Main product missing from cart response');
          data.variant_id = mainLine.variant_id;
          data.quantity = mainLine.quantity;
        }
        addedSuccessfully = true;

        if (theme.settings.afterAtc === 'page' || theme.settings.afterAtc === 'no-js') {
          // Allow the tick animation to complete
          setTimeout(() => {
            window.location.href = theme.routes.cart;
          }, 300);
        } else {
          // Update cart icon count.
          ProductForm.updateCartIcon(data);

          // If item was added from Quick Add drawer, show "Added to cart" message.
          const quickAddDrawer = this.closest('quick-add-drawer');
          if (quickAddDrawer) quickAddDrawer.addedToCart();

          setTimeout(() => {
            // Update cart drawer contents.
            if (this.cartDrawer) {
              this.cartDrawer.renderContents(
                data,
                !quickAddDrawer && theme.settings.afterAtc === 'drawer'
              );
            } else if (window.location.pathname === theme.routes.cart) {
              const cartItems = document.querySelector('cart-items');
              if (cartItems) {
                if (cartItems.dataset.empty === 'true') {
                  window.location.reload();
                } else {
                  cartItems.refresh();
                }
              }
            }
          }, 700);
        }

        const itemInOldCart = oldCartData.items.filter(
          (item) => item.variant_id === data.variant_id
        )[0];

        // Check if product was already in the cart
        if (itemInOldCart) {
          this.dispatchEvent(new CustomEvent('on:line-item:change', {
            bubbles: true,
            detail: {
              variantId: data.variant_id,
              oldQuantity: itemInOldCart.quantity,
              newQuantity: (itemInOldCart.quantity === data.quantity)
                ? itemInOldCart.quantity : data.quantity
            }
          }));
        } else {
          this.dispatchEvent(new CustomEvent('on:cart:add', {
            bubbles: true,
            detail: {
              variantId: data.variant_id
            }
          }));
        }
      } catch (error) {
        if (fitmentPicker) {
          if (!this.errorMsg || this.errorMsg.hidden) this.setErrorMsgState(fitmentPicker.dataset.errorMessage);
          try { await fetch(`${theme.routes.cart}.js`, { cache: 'no-store' }); } catch (_) { /* Cart state is unknown. */ }
          if (!this.cartDrawer) {
            const cartItems = document.querySelector('cart-items');
            if (cartItems && typeof cartItems.refresh === 'function') cartItems.refresh();
          }
        }
        console.log(error); // eslint-disable-line
        this.dispatchEvent(new CustomEvent('on:cart:error', {
          bubbles: true,
          detail: {
            error: this.errorMsg?.textContent
          }
        }));

        if (this.cartDrawer) this.cartDrawer.refresh(true);
      } finally {
        // Re-enable 'Add to Cart' button.
        if (!fitmentPicker || addedSuccessfully) this.submitBtn.classList.add('is-success');
        this.submitBtn.removeAttribute('aria-disabled');
        setTimeout(() => {
          this.submitBtn.classList.remove('is-loading');
          this.submitBtn.classList.remove('is-success');
        }, 1400);
      }
    }

    /**
     * Updates the cart icon count in the header.
     * @param {object} response - Response JSON.
     */
    static updateCartIcon(response) {
      const cartIconBubble = document.getElementById('cart-icon-bubble');
      if (cartIconBubble) {
        cartIconBubble.innerHTML = response.sections['cart-icon-bubble'];
      }
    }

    /**
     * Shows/hides an error message.
     * @param {string} [error=false] - Error to show a message for.
     */
    setErrorMsgState(error = false) {
      this.errorMsg = this.errorMsg || this.querySelector('.js-form-error');
      if (!this.errorMsg) return;

      this.errorMsg.hidden = !error;
      if (error) {
        this.errorMsg.innerHTML = '';
        const errorArray = Array.isArray(error) ? error : [error];
        errorArray.forEach((err, index) => {
          if (index > 0) this.errorMsg.insertAdjacentHTML('beforeend', '<br>');
          this.errorMsg.insertAdjacentText('beforeend', err);
        });
      }
    }
  }

  customElements.define('product-form', ProductForm);
}
